import { API_BASE_URL } from '../config';

import React, { useState, useEffect } from 'react';

const UserManagement = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', role: '', firstName: '', lastName: '', email: '', phoneNumber: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetNotice, setResetNotice] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE_URL}/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await res.json();
        console.log('User API response:', data); // Debug output
        if (!res.ok) {
          setError(data.message || 'Failed to fetch users');
        } else {
          setUsers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        setError('Network error');
      }
      setLoading(false);
    };
    fetchUsers();
  }, [token]);

  const closeResetModal = () => {
    setResetUser(null);
    setResetPassword('');
    setResetConfirm('');
    setResetError('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    if (resetPassword.length < 8) {
      setResetError('Password must be at least 8 characters.');
      return;
    }
    if (resetPassword !== resetConfirm) {
      setResetError('Passwords do not match.');
      return;
    }
    setResetLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/${resetUser._id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password: resetPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setResetError(data.message || 'Failed to reset password');
      } else {
        setResetNotice(`Password reset for ${data.username || resetUser.username}.`);
        closeResetModal();
      }
    } catch {
      setResetError('Network error');
    }
    setResetLoading(false);
  };

  // Handler for deleting a user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        alert('Failed to delete user');
        return;
      }
      setUsers(u => u.filter(u2 => u2._id !== userId));
    } catch {
      alert('Network error');
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '2rem auto' }}>
      <h2>User Management</h2>
      {loading && <p>Loading users...</p>}
  {error && <p style={{ color: 'red' }}>{error}</p>}
      {resetNotice && <p style={{ color: '#155724', background: '#d4edda', padding: '0.75rem', borderRadius: 4 }}>{resetNotice}</p>}
      <button style={{ padding: '0.5rem 1rem', marginBottom: '1rem' }} onClick={() => setShowModal(true)}>Add New User</button>

      {/* Add User Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350 }}>
            <h3>Add New User</h3>
            <form onSubmit={async e => {
              e.preventDefault();
              setFormError('');
              // Debug: log form state
              console.log('Form state:', form);
              // Validate all fields are non-empty after trimming
              const requiredFields = ['username', 'password', 'role', 'firstName', 'lastName', 'email', 'phoneNumber'];
              const missing = requiredFields.filter(f => {
                const val = form[f];
                // For role, check for empty string (dropdown default)
                if (f === 'role' && (!val || val === '')) return true;
                if (typeof val === 'undefined' || val === null) return true;
                if (typeof val === 'string' && val.trim() === '') return true;
                return false;
              });
              if (missing.length > 0) {
                setFormError(
                  'All fields are required.\n' +
                  'Missing: ' + missing.join(', ') + '\n' +
                  'Form: ' + JSON.stringify(form, null, 2)
                );
                return;
              } else {
                setFormError('');
              }
              setFormLoading(true);
              try {
                const res = await fetch(`${API_BASE_URL}/users`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify(form),
                });
                const data = await res.json();
                if (!res.ok) {
                  setFormError(data.message || 'Failed to create user');
                } else {
                  setShowModal(false);
                  setForm({ username: '', password: '', role: '', firstName: '', lastName: '', email: '', phoneNumber: '' });
                  setUsers(u => [...u, data.user]);
                }
              } catch (err) {
                setFormError('Network error');
              }
              setFormLoading(false);
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <label>Username:</label><br />
                <input type="text" value={form.username} required onChange={e => setForm(f => ({ ...f, username: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Password:</label><br />
                <input type="password" value={form.password} required onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Role:</label><br />
                <select value={form.role} required onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={{ width: '100%' }}>
                  <option value="">Select role</option>
                  <option value="Admin">Admin</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Receptionist">Receptionist</option>
                  <option value="Pharmacy">Pharmacy</option>
                  <option value="Therapist">Therapist</option>
                  <option value="Finance">Finance</option>
                  <option value="HR">HR</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>First Name:</label><br />
                <input type="text" value={form.firstName} required onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Last Name:</label><br />
                <input type="text" value={form.lastName} required onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Email:</label><br />
                <input type="email" value={form.email} required onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Phone Number:</label><br />
                <input type="text" value={form.phoneNumber} required onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ color: 'red', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
                {formError}
                {/* Always show current form state for debugging */}
                <br />
                <strong>Form:</strong> {JSON.stringify(form, null, 2)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button type="button" onClick={() => setShowModal(false)} disabled={formLoading}>Cancel</button>
                <button type="submit" disabled={formLoading}>{formLoading ? 'Creating...' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350 }}>
            <h3>Edit User</h3>
            <form onSubmit={async e => {
              e.preventDefault();
              setEditError('');
              setEditLoading(true);
              try {
                const res = await fetch(`${API_BASE_URL}/users/${editForm._id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify(editForm),
                });
                const data = await res.json();
                if (!res.ok) {
                  setEditError(data.message || 'Failed to update user');
                } else {
                  setEditModal(false);
                  setUsers(u => u.map(u2 => u2._id === editForm._id ? data.user : u2));
                }
              } catch (err) {
                setEditError('Network error');
              }
              setEditLoading(false);
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <label>Username:</label><br />
                <input type="text" value={editForm.username || ''} required onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Role:</label><br />
                <select value={editForm.role || ''} required onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} style={{ width: '100%' }}>
                  <option value="">Select role</option>
                  <option value="Admin">Admin</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Receptionist">Receptionist</option>
                  <option value="Pharmacy">Pharmacy</option>
                  <option value="Therapist">Therapist</option>
                  <option value="Finance">Finance</option>
                  <option value="HR">HR</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>First Name:</label><br />
                <input type="text" value={editForm.firstName || ''} required onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Last Name:</label><br />
                <input type="text" value={editForm.lastName || ''} required onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Email:</label><br />
                <input type="email" value={editForm.email || ''} required onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Phone Number:</label><br />
                <input type="text" value={editForm.phoneNumber || ''} required onChange={e => setEditForm(f => ({ ...f, phoneNumber: e.target.value }))} style={{ width: '100%' }} />
              </div>
              {editError && <div style={{ color: 'red', marginBottom: '1rem' }}>{editError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button type="button" onClick={() => setEditModal(false)} disabled={editLoading}>Cancel</button>
                <button type="submit" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350 }}>
            <h3>Reset Password</h3>
            <p style={{ marginTop: 0 }}>Set a new password for <strong>{resetUser.username}</strong>.</p>
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: '1rem' }}>
                <label>New password:</label><br />
                <input type="password" value={resetPassword} required minLength={8} onChange={e => setResetPassword(e.target.value)} style={{ width: '100%' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Confirm password:</label><br />
                <input type="password" value={resetConfirm} required minLength={8} onChange={e => setResetConfirm(e.target.value)} style={{ width: '100%' }} />
              </div>
              {resetError && <div style={{ color: 'red', marginBottom: '1rem' }}>{resetError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button type="button" onClick={closeResetModal} disabled={resetLoading}>Cancel</button>
                <button type="submit" disabled={resetLoading}>{resetLoading ? 'Saving...' : 'Reset Password'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Table */}
      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
          <thead>
            <tr style={{ background: '#eee' }}>
              <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Name</th>
              <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Email</th>
              <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Role</th>
              <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user._id}>
                <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{user.name || user.firstName}</td>
                <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{user.email}</td>
                <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{user.role}</td>
                <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>
                  <button style={{ marginRight: '0.5rem' }} onClick={() => {
                    setEditForm(user);
                    setEditModal(true);
                  }}>Edit</button>
                  <button style={{ marginRight: '0.5rem' }} onClick={() => {
                    setResetNotice('');
                    setResetError('');
                    setResetPassword('');
                    setResetConfirm('');
                    setResetUser(user);
                  }}>Reset Password</button>
                  <button onClick={() => handleDeleteUser(user._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UserManagement;
